import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './button';
import { cn } from '../../lib/utils';

/**
 * The shared Rows choices. CR-8186 added 2000; the backend list paginators
 * cap at the same value (helix_backend/pagination.py
 * ROWS_CONTROL_MAX_PAGE_SIZE), so every size offered here is served in full.
 */
export const PAGE_SIZE_OPTIONS = [25, 50, 100, 200, 500, 1000, 2000];

interface RowsSelectProps {
  value: number;
  options: number[];
  onChange: (size: number) => void;
}

/**
 * The compact "Rows:" select the list pager draws. Exported so a footer that
 * keeps its own layout (e.g. the Supplier Performance drill-down) shows the
 * same control rather than a look-alike.
 *
 * @param value - the page size in force
 * @param options - the sizes offered
 * @param onChange - called with the chosen size
 */
export function RowsSelect({ value, options, onChange }: RowsSelectProps) {
  return (
    <div className="flex items-center gap-1">
      <span className="text-xs">Rows:</span>
      <select
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="h-7 text-xs border rounded px-1 bg-background"
        data-testid="rows-per-page"
      >
        {options.map((size) => (
          <option key={size} value={size}>{size}</option>
        ))}
      </select>
    </div>
  );
}

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  itemsPerPage: number;
  /**
   * CR-7279: rows the server actually put on a page. The list paginators
   * now honour every size the Rows control offers (up to 2000), so this
   * normally equals `itemsPerPage`; if an endpoint still clamps, the page
   * count, the "Showing x-y" range AND the Rows select all follow the
   * served size — the control never claims a number the page does not hold.
   */
  servedPerPage?: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  itemLabel?: string; // e.g. "deliveries", "NCRs", "lines"
  /** Override the Rows dropdown choices (default 25-2000, the sizes every
   * list paginator serves in full). */
  pageSizeOptions?: number[];
  /** Extra classes, e.g. to sit above a table (`border-t-0 border-b mt-0 pt-0 pb-3 mb-3`). */
  className?: string;
}

export function Pagination({
  currentPage,
  totalPages,
  totalCount,
  itemsPerPage,
  servedPerPage,
  onPageChange,
  onPageSizeChange,
  itemLabel = 'items',
  pageSizeOptions = PAGE_SIZE_OPTIONS,
  className,
}: PaginationProps) {
  // Null safety - ensure values are defined
  const safeCurrentPage = currentPage || 1;
  const safeTotalPages = totalPages || 1;
  const safeTotalCount = totalCount || 0;
  const safeItemsPerPage = itemsPerPage || 10;
  const rowsOnPage = servedPerPage && servedPerPage > 0 ? Math.min(servedPerPage, safeItemsPerPage) : safeItemsPerPage;
  // The select shows the size in force. A clamped size is not in the
  // standard list, so it is added rather than leaving the control blank.
  const sizeOptions = pageSizeOptions.includes(rowsOnPage)
    ? pageSizeOptions
    : [...pageSizeOptions, rowsOnPage].sort((a, b) => a - b);

  if (safeTotalPages <= 1) return null;

  const startItem = (safeCurrentPage - 1) * rowsOnPage + 1;
  const endItem = Math.min(safeCurrentPage * rowsOnPage, safeTotalCount);

  return (
    <div className={cn('flex items-center justify-between border-t pt-4 mt-4', className)}>
      <div className="flex items-center gap-3 text-sm text-foreground">
        {onPageSizeChange && (
          <RowsSelect value={rowsOnPage} options={sizeOptions} onChange={onPageSizeChange} />
        )}
        <span>
          Page {safeCurrentPage} of {safeTotalPages} • {safeTotalCount.toLocaleString()} total {itemLabel}
          <span className="ml-2 text-xs">
            (Showing {startItem.toLocaleString()}-{endItem.toLocaleString()})
          </span>
        </span>
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(safeCurrentPage - 1)}
          disabled={safeCurrentPage === 1}
        >
          <ChevronLeft className="h-4 w-4" />
          Previous
        </Button>
        <div className="flex items-center gap-1">
          {Array.from({ length: Math.min(5, safeTotalPages) }, (_, i) => {
            let pageNum: number;
            if (safeTotalPages <= 5) {
              pageNum = i + 1;
            } else if (safeCurrentPage <= 3) {
              pageNum = i + 1;
            } else if (safeCurrentPage >= safeTotalPages - 2) {
              pageNum = safeTotalPages - 4 + i;
            } else {
              pageNum = safeCurrentPage - 2 + i;
            }
            return (
              <Button
                key={pageNum}
                variant={safeCurrentPage === pageNum ? 'default' : 'outline'}
                size="sm"
                onClick={() => onPageChange(pageNum)}
                className="w-8 h-8 p-0"
              >
                {pageNum}
              </Button>
            );
          })}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(safeCurrentPage + 1)}
          disabled={safeCurrentPage === safeTotalPages}
        >
          Next
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
